import { Flex, Text } from '@shared/components';

import { QSetSimilarityMatrix } from '../../components';
import { useQSetSimilarityResult } from '../../providers';
import { type QSetSimilarityResultContextData } from '../../providers/qset-similarity-result-provider/types';
import type { CheckType } from '../../types';
import {
  generateColumns,
  generateSingleStatusData,
  generateDraftToPublishedData,
  generateRows,
  getSlotProps,
} from '../../utils';

function getTitle(type: CheckType) {
  switch (type) {
    case 'DRAFT_TO_DRAFT':
      return 'Draft-to-Draft Duplicate Matrix';
    case 'DRAFT_TO_PUBLISHED':
      return 'Draft-to-Published Duplicate Matrix';
    case 'PUBLISHED_TO_PUBLISHED':
      return 'Published-to-Published Duplicate Matrix';
    default:
      return '';
  }
}

function getTransformedColumnsAndRows(
  type: CheckType,
  similarityData: QSetSimilarityResultContextData['similarityResult']['data'],
  uniqueQSetCodes: { drafts: string[]; published: string[] },
) {
  switch (type) {
    case 'DRAFT_TO_DRAFT':
      return generateSingleStatusData(similarityData, uniqueQSetCodes.drafts);
    case 'DRAFT_TO_PUBLISHED':
      return generateDraftToPublishedData(similarityData, uniqueQSetCodes);
    case 'PUBLISHED_TO_PUBLISHED':
      return generateSingleStatusData(similarityData, uniqueQSetCodes.published);
  }
}

export default function QSetSimilarityResult() {
  const { similarityResult, type, uniqueQSetCodes } = useQSetSimilarityResult();
  const [transformedColumns, transformedRows] = getTransformedColumnsAndRows(
    type,
    similarityResult.data,
    uniqueQSetCodes,
  );

  const columns = generateColumns(transformedColumns);
  const rows = generateRows(transformedRows, similarityResult.duplicationRate);
  const [columnSlotProps, rowSlotProps] = getSlotProps(type);
  return (
    <Flex
      sx={(theme) => ({
        gap: theme.spacing(4),
        flexDirection: 'column',
        textAlign: 'center',
        mx: 'auto',
      })}
    >
      <Text variant='h4'>{getTitle(type)} (%)</Text>
      <QSetSimilarityMatrix
        rows={rows}
        rowSlotProps={rowSlotProps}
        columns={columns}
        columnSlotProps={columnSlotProps}
      />
    </Flex>
  );
}
